import { create } from 'zustand';
import type { WalletSummary, Transaction } from '@auction/shared';
import { api } from '../lib/api';

interface DepositResult {
  newBalance: number;
  transactionId: string;
  message?: string;
}

interface WalletStore {
  wallet: WalletSummary | null;
  loading: boolean;
  error: string | null;
  fetchWallet: () => Promise<void>;
  getClientToken: () => Promise<string>;
  deposit: (amount: number, paymentNonce: string) => Promise<void>;
  depositWithNonce: (amount: number, paymentNonce: string) => Promise<DepositResult>;
  depositWithCard: (amount: number, cardNumber: string, expirationMonth: string, expirationYear: string, cvv: string) => Promise<DepositResult>;
  getTransactions: () => Promise<Transaction[]>;
}

export const useWalletStore = create<WalletStore>((set) => ({
  wallet: null,
  loading: false,
  error: null,

  fetchWallet: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get<{ success: boolean; data: { summary: WalletSummary } }>('/api/wallet');
      set({ wallet: response.data?.summary || null, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  getClientToken: async () => {
    const response = await api.post<{ success: boolean; data: { clientToken: string } }>('/api/wallet/deposit');
    if (!response.data?.clientToken) {
      throw new Error('Failed to get payment token');
    }
    return response.data.clientToken;
  },

  deposit: async (amount: number, paymentNonce: string) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post<{ success: boolean; data: { newBalance: number } }>('/api/wallet/deposit/confirm', {
        amount,
        paymentNonce,
      });
      set((state) => ({
        wallet: state.wallet ? { ...state.wallet, balance: response.data?.newBalance || state.wallet.balance } : null,
        loading: false,
      }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  depositWithNonce: async (amount: number, paymentNonce: string) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post<{ success: boolean; data: DepositResult }>('/api/wallet/deposit/confirm', {
        amount,
        paymentNonce,
      });
      set((state) => ({
        wallet: state.wallet ? { ...state.wallet, balance: response.data?.newBalance || state.wallet.balance } : null,
        loading: false,
      }));
      return response.data!;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  depositWithCard: async (amount: number, cardNumber: string, expirationMonth: string, expirationYear: string, cvv: string) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post<{ success: boolean; data: DepositResult }>('/api/wallet/deposit/card', {
        amount,
        cardNumber,
        expirationMonth,
        expirationYear,
        cvv,
      });
      set((state) => ({
        wallet: state.wallet ? { ...state.wallet, balance: response.data?.newBalance || state.wallet.balance } : null,
        loading: false,
      }));
      return response.data!;
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  getTransactions: async () => {
    const response = await api.get<{ success: boolean; data: { items: Transaction[] } }>('/api/wallet/transactions');
    return response.data?.items || [];
  },
}));
